import { Component, Ref } from '@eastsideco/salvo-ts';
import { cart } from "@/theme/Cart";
import { ThemeComponent } from "@/theme/ThemeComponent";
import { CartMain } from "../Cart/CartMain";
import { SideCart } from '../SideCart/SideCart';
import { formatMoney } from "@/utils/formatting";
import SimpleParallax from 'simple-parallax-js';
import ScrollMagic from 'scrollmagic';

export interface SelectedProducts {
    id: number;
    quantity: number;
}
@Component
export class LightDarkText extends ThemeComponent {
    @Ref({required: false}) button!: HTMLButtonElement;
    @Ref({required: false}) closeButton!: HTMLButtonElement;
    @Ref({required: false}) addToCart!: HTMLButtonElement;
    @Ref({required: false}) deselectAll!: HTMLButtonElement;
    @Ref({required: false}) drawer!: HTMLDivElement;
    @Ref({required: false}) overlay!: HTMLDivElement;
    @Ref({required: false}) multiSelectProduct!: HTMLInputElement[];
    @Ref({required: false}) totalAmount!: HTMLSpanElement;
    @Ref({required: false}) buttonText!: HTMLSpanElement;
    @Ref({required: false}) closeDrawerButton!: HTMLButtonElement;
    @Ref({required: false}) closeDrawerLink!: HTMLButtonElement;
    @Ref({required: false}) submitSingleItem!: HTMLButtonElement[];
    @Ref({required: false}) submitBundle!: HTMLButtonElement[];
    @Ref({required: false}) submitInfo!: HTMLDivElement;
    @Ref images!: HTMLElement;
    @Ref image!: HTMLElement[];

    protected quantityVal: number = 1;
    private section: HTMLDivElement | null = this.parentElement?.querySelector('.js-light-dark-text') ?? null;

    /**
     * Initialise.
     */
    init() {
        if (!this.button) { return; }

        this.$listen(this.button, 'click', (event) => {
            event.preventDefault();
            this.toggleDrawer();
        });

        if (this.closeButton) {
            this.$listen(this.closeButton, 'click', (event) => {
                event.preventDefault();
                this.closeDrawer();
            });
        }

        if(this.closeDrawerButton) {
            this.$listen(this.closeDrawerButton, 'click', (event) => {
                this.closeDrawer();
            });
        }

        if(this.closeDrawerLink) {
            this.$listen(this.closeDrawerLink, 'click', (event) => {
                this.closeDrawer();
            });
        }

        if (this.overlay) {
            this.$listen(this.overlay, 'click', (event) => {
                event.preventDefault();
                this.closeDrawer();
            });
        }

        if (this.addToCart) {
            this.$listen(this.addToCart, 'click', (event) => {
                event.preventDefault();
                void this.addProductsToCart();
            });
        }

        if (this.deselectAll) {
            this.$listen(this.deselectAll, 'click', (event) => {
                event.preventDefault();
                this.deselectAllProducts();
            });
        }

        // Add event listeners for checkbox changes to update total
        if (this.multiSelectProduct && this.multiSelectProduct.length > 0) {
            for (const checkbox of this.multiSelectProduct) {
                this.$listen(checkbox, 'change', () => {
                    this.updateTotal();
                });
            }
        }

        // Initialize total display
        this.updateTotal();

        // Add event listeners for single item submit buttons
        if (this.submitSingleItem && this.submitSingleItem.length > 0 ) {
            for (const submitButton of this.submitSingleItem) {
                this.$listen(submitButton, 'click', (event) => {
                    event.preventDefault();
                    const ids = submitButton.getAttribute('data-productId');
                    void this.addSingleProductToCart(Number(ids));
                });
            }
        }

        // Add event listeners for single item submit buttons
        if (this.submitBundle && this.submitBundle.length > 0 ) {
            for (const submitBundle of this.submitBundle) {
                this.$listen(submitBundle, 'click', (event) => {
                    event.preventDefault();
                    const ids = submitBundle.getAttribute('data-productId');
                    void this.addSingleProductToCart(Number(ids));
                });
            }
        }

    }

    toggleDrawer() {
        if(this.overlay) {
            this.overlay.classList.toggle('is-active');
        }
        if(this.drawer) {
            this.drawer.classList.toggle('is-active');
        }
        if(this.section) {
            this.section.classList.toggle('is-active');
        }
        document.body.classList.toggle('no-scroll');
    }

    closeDrawer() {
        if(this.overlay) {
            this.overlay.classList.remove('is-active');
        }
        if(this.drawer) {
            this.drawer.classList.remove('is-active');
        }
        if(this.section) {
            this.section.classList.remove('is-active');
        }
        if(this.submitInfo) {
            this.submitInfo.classList.add('hidden');
            setTimeout(() => {
                this.submitInfo.classList.remove('visible');
            }, 100);
        }
        if(this.deselectAll) {
            this.deselectAll.removeAttribute('disabled');
        }
        if(this.totalAmount) {
            this.totalAmount.style.display = 'inline';
        }
        if(this.buttonText) {
            this.buttonText.textContent = 'Add Selected to Cart';
        }
        document.body.classList.remove('no-scroll');
        this.deselectAllProducts();
    }

    deselectAllProducts() {
        for (const product of this.multiSelectProduct) {
            product.checked = false;
        }
        this.updateTotal();
    }

    private updateTotal() {
        if (!this.totalAmount) {return;}

        let total = 0;
        let selectedCount = 0;

        for (const checkbox of this.multiSelectProduct) {
            if (checkbox.checked) {
                selectedCount++;
                // Find the product card containing this checkbox
                const productCard = checkbox.closest('.light-dark-text__label');
                if (productCard) {
                    // Find the price element within the product card
                    const priceElement = productCard.querySelector('.product-price__money');
                    if (priceElement) {
                        const priceText = priceElement.textContent || '';
                        // Extract numeric value from price (remove currency symbol and commas)
                        const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                        if (priceMatch) {
                            const price = parseFloat(priceMatch[0].replace(/,/g, ''));
                            if (!isNaN(price)) {
                                // Convert to cents (Shopify stores prices in cents)
                                total += price * 100;
                            }
                        }
                    }
                }
            }
        }

        // Update button text and total display
        if (selectedCount > 0) {
            const formattedTotal = formatMoney(total) as string;
            this.totalAmount.textContent = ` | ${formattedTotal}`;
            this.totalAmount.style.display = 'inline';
        } else {
            this.totalAmount.style.display = 'none';
        }
    }

    protected async addProductsToCart() {
        const selectedProductsArr = [];

        for (const product of this.multiSelectProduct) {
            if (product.checked) {
                const selectedProductsObj = {} as SelectedProducts;
                // Extract the product variant ID from the checkbox ID
                // The ID format is: "section-id-variant-id"
                const idParts = product.id.split('-');
                const variantId = idParts[idParts.length - 1]; // Get the last part which is the variant ID

                selectedProductsObj.id = Number(variantId);
                selectedProductsObj.quantity = 1;
                selectedProductsArr.push(selectedProductsObj);
            }
        }

        await cart.addItems(selectedProductsArr)
            .then((items) => {
                if(this.submitInfo) {
                    this.submitInfo.classList.remove('hidden');
                    setTimeout(() => {
                        this.submitInfo.classList.add('visible');
                    }, 100);
                }
                if(this.deselectAll) {
                    this.deselectAll.setAttribute('disabled', 'disabled');
                }
                if(this.totalAmount) {
                    this.totalAmount.style.display = 'none';
                }
                if(this.buttonText) {
                    this.buttonText.textContent = 'Products Added';
                }
                // Dispatch event for other components to listen to
                document.dispatchEvent(new Event('cartChangedLightDarkText'));
            })

            .catch((event: unknown | null) => {
                if (event instanceof Error) {
                    this.showError(event.message);
                }
            });
    }

    protected showError(msg: string | null): void {
        this.$log.info(msg?msg:'');
    }

    protected async addSingleProductToCart(ids: number) {
        try {
            const idsToAdd: { id: number; quantity: number }[] = [];
            const cartCheck: { id: number; inCart: boolean }[] = [];
            const idUse = ids; // ids is already a number, no need to parse
            const itemInCart: boolean = cart.checkCart(idUse);
            const addString = { id: idUse, quantity: 1 };
            const checkString = { id: idUse, inCart: itemInCart };
            cartCheck.push(checkString);
            idsToAdd.push(addString);

            await cart.addItems(idsToAdd)
                .then(() => {
                    if(this.submitInfo) {
                        this.submitInfo.classList.remove('hidden');
                        setTimeout(() => {
                            this.submitInfo.classList.add('visible');
                        }, 100);
                        document.dispatchEvent(new Event('cartChangedLightDarkText'));
                    } else {
                        document.dispatchEvent(new Event('cartChanged'));
                    }
                })
                .catch((error: unknown) => {
                    this.$log.error('Cart error', { error });
                });
        } catch (error) {
            this.$log.error('Unexpected cart error', { error });
        }
    }
}
